import { useState, useEffect, useCallback } from 'react';
import { 
  getQuestionnaires, 
  getQuestionnaire,
  getDefaultQuestionnaire,
  saveQuestionnaire, 
  updateQuestionnaire,
  deleteQuestionnaire,
  setDefaultQuestionnaire,
  validateQuestionnaire
} from '@/lib/firebase/queries/questionnaires';
import type { Questionnaire, Question, QuestionnaireValidation } from '@/types/questionnaire';
import { HABIT_CATEGORY_ORDER } from '@/lib/constants/habitBadges';
import { toast } from 'sonner';

export function useQuestionnaires() {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [defaultQuestionnaire, setDefaultQuestionnaireState] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchQuestionnaires = useCallback(async () => {
    try {
      setLoading(true);
      const [allQuestionnaires, defaultQ] = await Promise.all([
        getQuestionnaires(),
        getDefaultQuestionnaire()
      ]);
      setQuestionnaires(allQuestionnaires);
      setDefaultQuestionnaireState(defaultQ);
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
      toast.error('Failed to fetch questionnaires');
    } finally {
      setLoading(false);
    }
  }, []);

  const createQuestionnaire = async (
    name: { en: string; ar: string }, 
    description: { en: string; ar: string } | undefined,
    userId: string,
    isDefault = false
  ): Promise<string> => {
    try {
      setSaving(true);
      
      // Create a new questionnaire with empty questions for each category (2 options by default)
      const questions: Question[] = HABIT_CATEGORY_ORDER.map((category, index) => ({
        id: crypto.randomUUID(),
        question: { en: '', ar: '' },
        options: [
          { id: crypto.randomUUID(), text: { en: '', ar: '' }, score: 1 },
          { id: crypto.randomUUID(), text: { en: '', ar: '' }, score: 0 }
        ],
        order: index,
        category
      }));

      const newQuestionnaire: any = {
        name,
        isDefault,
        questions,
        createdBy: userId
      };
      
      // Only add description if it's not undefined
      if (description !== undefined) {
        newQuestionnaire.description = description;
      }

      const id = await saveQuestionnaire(newQuestionnaire, userId, true); // Allow empty for new questionnaires
      await fetchQuestionnaires();
      toast.success('Questionnaire created successfully');
      return id;
    } catch (error) {
      console.error('Error creating questionnaire:', error);
      toast.error('Failed to create questionnaire');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateQuestionnaireData = async (id: string, updates: Partial<Questionnaire>): Promise<void> => {
    try {
      setSaving(true);
      await updateQuestionnaire(id, updates);
      await fetchQuestionnaires();
      toast.success('Questionnaire updated successfully');
    } catch (error) {
      console.error('Error updating questionnaire:', error);
      toast.error('Failed to update questionnaire');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteQuestionnaireById = async (id: string): Promise<void> => {
    try {
      setSaving(true);
      await deleteQuestionnaire(id);
      await fetchQuestionnaires();
      toast.success('Questionnaire deleted successfully');
    } catch (error) {
      console.error('Error deleting questionnaire:', error);
      toast.error('Failed to delete questionnaire');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const setAsDefault = async (id: string): Promise<void> => {
    try {
      setSaving(true);
      await setDefaultQuestionnaire(id);
      await fetchQuestionnaires();
      toast.success('Default questionnaire updated');
    } catch (error) {
      console.error('Error setting default questionnaire:', error);
      toast.error('Failed to set default questionnaire');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const validateQuestionnaireData = (questionnaire: Questionnaire): QuestionnaireValidation => {
    return validateQuestionnaire(questionnaire);
  };

  useEffect(() => {
    fetchQuestionnaires();
  }, [fetchQuestionnaires]);

  return {
    questionnaires,
    defaultQuestionnaire,
    loading,
    saving,
    createQuestionnaire,
    updateQuestionnaire: updateQuestionnaireData,
    deleteQuestionnaire: deleteQuestionnaireById,
    setDefaultQuestionnaire: setAsDefault,
    validateQuestionnaire: validateQuestionnaireData,
    refreshQuestionnaires: fetchQuestionnaires
  };
}
